import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '@core/services/auth';
import { StoragePrefix } from '@core/constant';
import { StorageService } from 'src/app/utils';
import { endpoints } from '@core/config';
import { EndpointConfig } from '@core/config/endpoints/endpoint.model';
import { environment } from '@env/environment';

const endpointByAlias = new Map<string, EndpointConfig>(
  endpoints.map((e) => [e.alias, e])
);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // 🔹 Whitelist for static assets & special endpoints
  const whitelist = [
    '/en.json',
    '/ar.json',
    '/generate-pdf',
    'https://api64.ipify.org',
    'https://maps.googleapis.com/maps/api/geocode/json',
    'https://www.seulah.sa/pdfs/Seulah_Certificate.pdf'
  ];
  const _authService = inject(AuthService);
  const _storage = inject(StorageService);

  // 1️⃣ Get both tokens
  const fullToken = _storage.get(StoragePrefix.TOKEN);
  const signupTempToken = _authService.token; // signal or variable set during signup

  // 2️⃣ Pick whichever token exists
  const tokenToUse = fullToken || signupTempToken;
  const lang = _storage.get(StoragePrefix.LANGUAGE);
  const isWhitelisted = whitelist.some(u => isRequest(req, u));
  // 🔹 Skip interceptor for whitelisted URLs
  if (isWhitelisted) {
    return next(req);
  }
  // 🔹 Find endpoint config by alias (Map lookup)
  const cleanUrl = req.url.split('?')[0].replace(/^\//, '');
  const endpoint = endpointByAlias.get(cleanUrl);
  if (!endpoint) {
    return next(req);
  }

  let modifiedReq = req;
  const envConfig: any = environment[endpoint?.baseUrl as keyof typeof environment];
  if (!envConfig) {
    return next(req);
  }

  const fullUrl =
    `${envConfig.baseUrl}` +
    `${envConfig.apiVersion}` +
    `${endpoint?.path}/` +
    `${endpoint?.name}`;

  modifiedReq = req.clone({ url: fullUrl });
  const caseUuid = _storage.get(StoragePrefix.KEY);
  const baseHeaders: Record<string, string> = {
    Accept: 'application/json',
    'ngrok-skip-browser-warning': 'true',
    'gateway-api-key': environment.gatewayApiKey
  };

  if (lang) {
    baseHeaders['Accept-Language'] = lang;
  }

  if (caseUuid) {
    baseHeaders['x-case-uuid'] = caseUuid;
  }

  // 3️⃣ Clone request and set headers
  if (tokenToUse) {

    const authReq = modifiedReq.clone({
      setHeaders: {
        ...baseHeaders,
        Authorization: tokenToUse, // Capitalized header name (matches spec)
        'x-mod-id': '0',
      },
    });
    return next(authReq);
  } else {
    const authReq = modifiedReq.clone({
      setHeaders: {
        ...baseHeaders,
        'X-API-KEY': environment.xApiKey,
      },
    });
    return next(authReq);
  }
};

function isRequest(request: HttpRequest<any>, reqName: string): boolean {
  // Remove query params for matching
  const urlWithoutParams = request.url.split('?')[0];
  // Remove trailing slash
  const normalizedUrl = urlWithoutParams.replace(/\/$/, '');
  return normalizedUrl.endsWith(reqName);
}
